import { createContext, memo, type ReactNode, useContext } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { type BoardPageBoard } from '~/store/centralizedBoard/types';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const PublicRootBoardContext = createContext<BoardPageBoard | null>(null);

export type PublicRootBoardProviderProps = {
  children: ReactNode;
  /** The root shared board the share link points to. Immutable for the session. */
  board: BoardPageBoard;
};

/**
 * Provides the ROOT shared board for a public board page — the board the share link targets, which is
 * stable for the session. This is distinct from the centralized (currently-viewed) board in redux,
 * which changes as the viewer navigates into sub-boards. Consumers that need "the share" (e.g. the
 * topbar "Save all" download, which must target the whole share, not the current sub-board) read it
 * here instead of `centralizedBoardSelector`.
 */
export const PublicRootBoardProvider = memo(({ board, children }: PublicRootBoardProviderProps) => (
  <PublicRootBoardContext.Provider value={board}>{children}</PublicRootBoardContext.Provider>
));

PublicRootBoardProvider.displayName = 'PublicRootBoardProvider';

export const usePublicRootBoard = (): BoardPageBoard | null => {
  const board = useContext(PublicRootBoardContext);

  if (board === null) {
    const error = new Error('usePublicRootBoard must be used within a PublicRootBoardProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({ error, context: 'Public root board context not found' });
  }

  return board;
};
